import { Launch as ExportIcon } from "@mui/icons-material";
import { SxProps } from "@mui/material";
import ButtonOrMenuItem from "atoms/ButtonOrMenuItem";
import { CloseMenuType } from "atoms/SimpleMenu";
import _uniq from "lodash/uniq";
import EdmDialog from "molecules/EdmDialog";
import { Fragment, useState } from "react";
import { ButtonProps, LinearProgress, useTranslate } from "react-admin";

import ExportDialog, { ExportDialogPropsType } from "./ExportDialog";

type ExportDialogButtonPropsType = {
  fetchEntities?: () => Promise<void>;
  closeMenu?: CloseMenuType;
  variant?: ButtonProps["variant"];
  sx?: SxProps;
  color?: ButtonProps["color"];
  size?: ButtonProps["size"];
  label?: string;
  selectedIds?: number[];
  resource: string;
} & ExportDialogPropsType;

export default function ExportDialogButton(props: ExportDialogButtonPropsType) {
  const {
    fetchEntities = undefined,
    closeMenu,
    variant,
    color,
    size,
    sx,
    label,
    selectedIds,
    resource,
    ...rest
  } = props;
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const translate = useTranslate();

  const closeDialog = () => setDialogIsOpen(false);

  const openDialog = async () => {
    if (fetchEntities) {
      setDialogIsOpen(true);
      setIsLoading(true);

      try {
        await fetchEntities();
      } catch {
        setHasLoadingError(true);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
      setDialogIsOpen(true);
    }
  };

  const uniqIdsCount = selectedIds ? _uniq(selectedIds).length : 0;
  const translatedResource = translate(`resources.${resource}.name`, {
    smart_count: uniqIdsCount,
  });
  const defaultLabel = translate("export.actions.resource", {
    resource: translatedResource,
  });

  return (
    <Fragment>
      <ButtonOrMenuItem
        onClick={openDialog}
        label={label || "export.actions.export"}
        labelOpts={{ resource: translatedResource }}
        closeMenu={closeMenu}
        data-testid={`select-export-fields-${closeMenu ? "menu-item" : "button"}`}
        sx={sx}
        variant={variant}
        color={color}
        size={size}
      >
        <ExportIcon />
      </ButtonOrMenuItem>

      <EdmDialog
        maxWidth="lg"
        fullWidth
        open={dialogIsOpen}
        onClose={closeDialog}
        title={
          hasLoadingError ? translate("export.error") : `${!isLoading && selectedIds && uniqIdsCount} ${defaultLabel}`
        }
      >
        {() => (
          <>
            {(isLoading || (!isLoading && hasLoadingError)) && (
              <>
                {isLoading && <LinearProgress timeout={0} />}
                {!isLoading && hasLoadingError && <p>{translate("ra.message.error")}</p>}
              </>
            )}

            {!isLoading && !hasLoadingError && dialogIsOpen && (
              <ExportDialog resource={resource} selectedIds={selectedIds} {...rest} />
            )}
          </>
        )}
      </EdmDialog>
    </Fragment>
  );
}
